import React from "react";
import { useTranslation } from "react-i18next";
import MetricCard from "../components/MetricCard";
import InfoBox from "../components/InfoBox";
import StatisticCard from "../components/StatisticCard";
import PercentileChart from "../components/PercentileChart";
import DeviationDartboard from "../components/DeviationBoard";
import ProMatchSimulationSection from "../components/ProMatchSimulationSection";
import {
  TrendUpIcon,
  CheckIcon,
  BarChartIcon,
} from "../components/StatisticsIcons";
import { formatValue } from "../../../utils/common";

/**
 * My Statistics Tab Komponente (Pro Feature)
 * Zeigt erweiterte personalisierte Statistiken für Pro-Benutzer
 */
const MyStatisticsTab = ({
  proStatistics,
  statistics,
  showInfoBox,
  onDismissInfoBox,
  isProMember,
  renderBlurredContent,
}) => {
  const { t } = useTranslation();

  if (!isProMember) {
    return renderBlurredContent(t("statistics.tabs.myStatistics"));
  }

  const {
    scoringAvgT20,
    avgLower,
    avgMedian,
    avgUpper,
    dqLower,
    dqMedian,
    dqUpper,
    xStdDeviation,
    yStdDeviation,
    phi,
    generalScore,
  } = proStatistics;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Info Box */}
      <InfoBox
        title={t("statistics.myStatistics.infobox_title")}
        message={t("statistics.myStatistics.infobox_text")}
        show={showInfoBox}
        onDismiss={onDismissInfoBox}
        color="yellow"
      />

      {/* Key Metrics Section */}
      <div className="grid grid-cols-3 gap-3 sm:gap-6 lg:gap-8 mb-8">
        <MetricCard
          icon={<BarChartIcon />}
          title={t("statistics.myStatistics.gamePerformance")}
          hint={t("statistics.myStatistics.gamePerformanceHint")}
          className="pt-4 pb-6 min-h-[120px] sm:min-h-[140px]"
        >
          <div className="w-full mt-2">
            <PercentileChart
              lowerPercentile={avgLower}
              averagePercentile={avgMedian}
              upperPercentile={avgUpper}
            />
          </div>
        </MetricCard>
        
        <MetricCard
          icon={<TrendUpIcon />}
          title={t("statistics.myStatistics.scoringAverage")}
          value={scoringAvgT20?.toFixed(2)}
          hint={t("statistics.myStatistics.scoringAverageHint")}
          className="min-h-[120px] sm:min-h-[140px]"
        />

        <MetricCard
          icon={<CheckIcon />}
          title={t("statistics.myStatistics.doubleOutSuccess")}
          hint={t("statistics.myStatistics.doubleOutHint")}
          className="pt-4 pb-6 min-h-[120px] sm:min-h-[140px]"
        >
          <div className="w-full mt-2">
            <PercentileChart
              lowerPercentile={dqLower}
              averagePercentile={dqMedian}
              upperPercentile={dqUpper}
            />
          </div>
        </MetricCard>
      </div>

      {/* Throwing Pattern Analysis */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <StatisticCard
          title={t("statistics.myStatistics.throwingPatternAnalysis")}
          hint={t("statistics.myStatistics.throwingPatternHint")}
        >
          <div className="bg-secondary/30 rounded-lg p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Dartboard visualization */}
              <div className="relative w-full max-w-full">
                <DeviationDartboard
                  xDeviation={formatValue(xStdDeviation)}
                  yDeviation={formatValue(yStdDeviation)}
                  phi={formatValue(phi)}
                  maxDeviation={50}
                />
              </div>

              {/* Stats and insights */}
              <div className="flex flex-col justify-between">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-test text-sm font-medium mb-3">
                      {t("statistics.myStatistics.deviationAnalysis")}
                    </h4>

                    <MetricCard
                      icon={<BarChartIcon />}
                      title={t("statistics.myStatistics.score")}
                      value={formatValue(generalScore)}
                      hint={t("statistics.myStatistics.scoreHint")}
                      className="bg-secondary/40"
                    />
                  </div>

                  {/* Pattern Insights */}
                  <div className="bg-secondary/30 rounded-lg p-4">
                    <h4 className="text-test text-sm font-medium mb-2">
                      {t("statistics.myStatistics.patternInsights")}
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start space-x-2">
                        <span className="text-test">•</span>
                        <span className="text-white/80">
                          {xStdDeviation > yStdDeviation
                            ? t("statistics.myStatistics.horizontalLarger")
                            : t("statistics.myStatistics.verticalLarger")}
                        </span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-test">•</span>
                        <span className="text-white/80">
                          {Math.abs(phi) > 5
                            ? t("statistics.myStatistics.significantRotation", {
                                value: formatValue(Math.abs(phi)),
                              })
                            : t("statistics.myStatistics.goodRotation")}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </StatisticCard>
      </div>

      {/* Pro Match Simulation */}
      <StatisticCard
        title="Pro Matchup Simulation"
        hint="Choose a pro player and see how you'd perform in a simulated match based on your stats."
      >
        <ProMatchSimulationSection statistics={statistics} />
      </StatisticCard>
    </div>
  );
};

export default MyStatisticsTab;
